/**
 * The briefing's inline calendar: the attorney's day at a glance. A ribbon
 * from 8 AM to 6 PM with the now marker, the all-day pills, earlier events
 * folded behind a one-line summary, the red now line, then what's left with
 * an "In N min" badge on the next thing.
 */
import dayjs from "dayjs";
import { Bell, ChevronRight } from "lucide-react";
import { useMemo, useState, type FC } from "react";

import {
  EVENT_COLOR_CLASSES,
  EventPill,
  formatTimeShort,
  type CalendarEvent
} from "@/features/calendar";
import { cn } from "@/lib/utils";

import { ClientAvatar } from "./ClientAvatar";
import { SourceMark } from "./CalendarRailTab";

export const RIBBON_FROM = 8;
export const RIBBON_TO = 18;
const TICKS = [8, 10, 12, 14, 16, 18];
const AGENDA_COLS = "52px 3px minmax(0, 1fr) auto";

const hourOf = (d: Date): number => d.getHours() + d.getMinutes() / 60;

const fmtHour = (h: number): string => {
  const whole = Math.floor(h);
  const minutes = Math.round((h - whole) * 60);
  const d = new Date(2000, 0, 1, whole, minutes);
  return minutes ? dayjs(d).format("h:mm A") : dayjs(d).format("h A");
};

export const untilLabel = (start: Date, now: Date): string => {
  const m = Math.max(0, Math.round((start.getTime() - now.getTime()) / 60000));
  if (m < 60) return `In ${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `In ${h} hr${rest ? ` ${rest} min` : ""}`;
};

export const lengthLabel = (e: CalendarEvent): string => {
  if (e.kind === "REMINDER") return "Due";
  const m = Math.round((e.end.getTime() - e.start.getTime()) / 60000);
  if (m >= 60) {
    const h = m / 60;
    return `${Number.isInteger(h) ? h : h.toFixed(1)} hr`;
  }
  return `${m} min`;
};

const barClass = (e: CalendarEvent): string =>
  EVENT_COLOR_CLASSES[e.color]?.dot ?? "bg-muted-foreground/40";

const DayRibbon: FC<{ events: CalendarEvent[]; now: Date }> = ({
  events,
  now
}) => {
  const pct = (h: number) =>
    `${
      ((Math.min(Math.max(h, RIBBON_FROM), RIBBON_TO) - RIBBON_FROM) /
        (RIBBON_TO - RIBBON_FROM)) *
      100
    }%`;
  const nowH = hourOf(now);
  return (
    <div className="relative h-8.5" data-testid="day-ribbon">
      <div className="absolute inset-x-0 top-1.5 h-3 rounded bg-muted" />
      <div
        className="absolute top-1.5 left-0 h-3 rounded-l bg-fill-1"
        style={{ width: pct(nowH) }}
      />
      {events.map((e) => {
        const s0 = hourOf(e.start);
        const en = e.kind === "REMINDER" ? s0 + 0.12 : hourOf(e.end);
        return (
          <span
            key={e.id}
            title={`${formatTimeShort(e.start)} ${e.title}`}
            className={cn("absolute top-1.5 h-3 rounded-xs", barClass(e))}
            style={{
              left: pct(s0),
              width: `calc(${pct(en)} - ${pct(s0)} - 1px)`,
              opacity: en <= nowH ? 0.35 : 1
            }}
          />
        );
      })}
      <span
        className="absolute top-0.5 -ml-px h-5 w-0.5 rounded-xs bg-destructive"
        style={{ left: pct(nowH) }}
      />
      {TICKS.map((h, i) => (
        <span
          key={h}
          className={cn(
            "absolute top-5.5 text-xs whitespace-nowrap tabular-nums text-fg-3",
            i === 0
              ? ""
              : i === TICKS.length - 1
              ? "-translate-x-full"
              : "-translate-x-1/2"
          )}
          style={{ left: pct(h) }}
        >
          {fmtHour(h)}
        </span>
      ))}
    </div>
  );
};

const AgendaRow: FC<{
  event: CalendarEvent;
  now: Date;
  upNext?: boolean;
  past?: boolean;
  onClick: () => void;
}> = ({ event, now, upNext = false, past = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="agenda-row"
    className={cn(
      "grid w-full items-center gap-x-3 px-3.5 py-2 text-left hover:bg-accent/60",
      past && "opacity-50"
    )}
    style={{ gridTemplateColumns: AGENDA_COLS }}
  >
    <span className="self-start text-right text-xs leading-5 tabular-nums text-muted-foreground">
      {formatTimeShort(event.start)}
    </span>
    <span className={cn("h-8.5 w-0.75 rounded-sm", barClass(event))} />
    <span className="min-w-0">
      <span className="flex min-w-0 items-center gap-1.5">
        {event.kind === "REMINDER" ? (
          <Bell className="size-3.25 shrink-0 text-muted-foreground" />
        ) : null}
        <span className="truncate text-sm leading-5 font-medium text-foreground">
          {event.title}
        </span>
        {upNext ? (
          <span
            className="inline-flex h-4.5 shrink-0 items-center rounded bg-primary/10 px-1.5 text-xs font-medium text-primary"
            data-testid="up-next"
          >
            {untilLabel(event.start, now)}
          </span>
        ) : null}
      </span>
      <span className="mt-px flex min-w-0 items-center gap-1.5 text-xs leading-4 text-muted-foreground">
        {event.clientName ? (
          <>
            <span className="inline-flex shrink-0 items-center gap-1">
              <ClientAvatar name={event.clientName} size={14} />
              <span className="text-foreground">{event.clientName}</span>
            </span>
            <span>·</span>
          </>
        ) : null}
        <span className="shrink-0">{lengthLabel(event)}</span>
        {event.location ? (
          <>
            <span>·</span>
            <span className="truncate">{event.location}</span>
          </>
        ) : null}
      </span>
    </span>
    <span className="inline-flex items-center gap-1.5">
      {event.source !== "reminder" ? (
        <SourceMark source={event.source} />
      ) : null}
    </span>
  </button>
);

type Props = {
  /** Today's events for the attorney (timed and all-day). */
  events: CalendarEvent[];
  now?: Date;
  onOpen: (event?: CalendarEvent) => void;
  className?: string;
};

export const BriefingAgenda: FC<Props> = ({
  events,
  now = new Date(),
  onOpen,
  className
}) => {
  const [showEarlier, setShowEarlier] = useState(false);
  const { timed, allDay, earlier, later } = useMemo(() => {
    const timed = events
      .filter((e) => !e.allDay)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    const allDay = events.filter((e) => e.allDay);
    const earlier = timed.filter((e) => e.end.getTime() <= now.getTime());
    const later = timed.filter((e) => !earlier.includes(e));
    return { timed, allDay, earlier, later };
  }, [events, now]);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2",
        className
      )}
      data-testid="briefing-agenda"
    >
      <div className="px-3.5 pt-3 pb-1.5">
        <DayRibbon events={timed} now={now} />
      </div>
      {allDay.length ? (
        <div
          className="grid items-center gap-x-3 px-3.5 pt-1 pb-2.5"
          style={{ gridTemplateColumns: AGENDA_COLS }}
        >
          <span className="text-right text-xs text-muted-foreground">
            All day
          </span>
          <span />
          <div className="col-span-2 flex min-w-0 flex-wrap gap-1">
            {allDay.map((e) => (
              <EventPill key={e.id} event={e} onOpen={onOpen} hideTime />
            ))}
          </div>
        </div>
      ) : null}
      <div className="border-t border-border py-1">
        {earlier.length ? (
          <>
            <button
              type="button"
              onClick={() => setShowEarlier((v) => !v)}
              data-testid="agenda-earlier"
              className="grid w-full items-center gap-x-3 px-3.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/60"
              style={{ gridTemplateColumns: AGENDA_COLS }}
            >
              <span className="flex justify-end">
                <ChevronRight
                  className={cn(
                    "size-3 transition-transform",
                    showEarlier && "rotate-90"
                  )}
                />
              </span>
              <span />
              <span className="col-span-2 truncate">
                {earlier.length} earlier today:{" "}
                {earlier.map((e) => e.title).join(", ")}
              </span>
            </button>
            {showEarlier
              ? earlier.map((e) => (
                  <AgendaRow
                    key={e.id}
                    event={e}
                    now={now}
                    past
                    onClick={() => onOpen(e)}
                  />
                ))
              : null}
          </>
        ) : null}
        <div
          className="grid items-center gap-x-3 px-3.5 py-0.5"
          style={{ gridTemplateColumns: AGENDA_COLS }}
          data-testid="agenda-now"
        >
          <span className="text-right text-xs font-medium tabular-nums text-destructive">
            {formatTimeShort(now)}
          </span>
          <span className="-ml-0.5 size-1.75 rounded-full bg-destructive" />
          <span className="col-span-2 h-px bg-destructive/60" />
        </div>
        {later.map((e, i) => (
          <AgendaRow
            key={e.id}
            event={e}
            now={now}
            upNext={i === 0}
            onClick={() => onOpen(e)}
          />
        ))}
        {timed.length === 0 && allDay.length === 0 ? (
          <div className="px-3.5 py-3 text-center text-xs text-muted-foreground">
            Nothing on your calendar today.
          </div>
        ) : null}
      </div>
    </div>
  );
};
